import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTenants } from '../../hooks/useTenants';
import { getDayOpenStatus, runDayOpen } from '../../api/dayOpen';
import { Loader2 } from 'lucide-react';
import { ApiError } from '../../api/client';
import { showErrorToast } from '../../components/ErrorToast';
import type { DayOpenStatus, DayOpenRunParams, DayOpenRun } from '../../types/DayOpen';
import type { Tenant } from '../../types/Tenant';
import { ValuationParamsModal } from './ValuationParamsModal';
import { DayOpenConfirmModal } from './DayOpenConfirmModal';
import { findMissingPrereqs, type MissingGroup } from './dayOpenPrereqs';
import { refreshValuationParams } from '../../hooks/useValuationParams';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './DayOpenPage.css';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';

// Day-Open operations screen — one self-contained panel per tenant. Each panel
// fetches/runs against its own tenant via an explicit X-Tenant-Id (see
// src/api/dayOpen.ts). There is no bulk endpoint: "open all" fires N
// independent runDayOpen calls. Day-open runs in the BACKGROUND (POST -> 202 {runId});
// each panel polls the status endpoint (latestRun) every ~3s until it finishes.

function formatDate(iso: string | null): string {
  return formatDateTimeIL(iso) || '—';
}

function formatDateOnlyOrDash(iso: string | null): string {
  return formatDateOnly(iso) || '—';
}

function formatBankImport(status: DayOpenStatus): string {
  if (!status.lastBankImportAt) return t('dayOpen.noBankImport');
  const when = formatDateTimeIL(status.lastBankImportAt);
  return status.lastBankImportValueDate
    ? `${when} (${t('dayOpen.dataOf')} ${formatDateOnlyOrDash(status.lastBankImportValueDate)})`
    : when;
}

interface PanelState {
  status: DayOpenStatus | null;
  loading: boolean;
  error: string | null;
  running: boolean;
  runError: string | null;
}

const INITIAL_PANEL_STATE: PanelState = {
  status: null,
  loading: true,
  error: null,
  running: false,
  runError: null,
};

const POLL_INTERVAL_MS = 3000;

function isActive(run: DayOpenRun | null | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

interface TenantPanelHandle {
  refresh: () => Promise<void>;
  tenantId: number;
  getStatus: () => DayOpenStatus | null;
  // Resolves true when the run succeeded, false when it failed (or could not be queued).
  run: (params?: DayOpenRunParams) => Promise<boolean>;
}

interface TenantPanelProps {
  tenant: Tenant;
  registerHandle: (tenantId: number, handle: TenantPanelHandle | null) => void;
  onRequestRun: (tenant: Tenant, status: DayOpenStatus) => void | Promise<void>;
}

function TenantPanel({ tenant, registerHandle, onRequestRun }: TenantPanelProps) {
  const [state, setState] = useState<PanelState>(INITIAL_PANEL_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const mountedRef = useRef(true);
  // One poll loop per panel: a second caller (e.g. a 409 while resuming) joins the running one.
  const pollRef = useRef<Promise<boolean> | null>(null);
  // Local clock reading at the moment the latest status arrived; elapsed = server elapsedMs + time since.
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());
  const [, setTick] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyStatus = useCallback((status: DayOpenStatus) => {
    setFetchedAt(Date.now());
    setState((p) => ({ ...p, status, loading: false, error: null }));
  }, []);

  // Polls the status endpoint every ~3s until the latest run is no longer queued/running.
  // A transient poll failure (server restarting...) does not end the run - keep trying.
  const pollUntilDone = useCallback((): Promise<boolean> => {
    if (pollRef.current) return pollRef.current;
    const loop = (async (): Promise<boolean> => {
      setState((p) => ({ ...p, running: true }));
      try {
        for (;;) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (!mountedRef.current) return false;
          let status: DayOpenStatus;
          try {
            status = await getDayOpenStatus(tenant.tenantId);
          } catch {
            continue;
          }
          if (!mountedRef.current) return false;
          applyStatus(status);
          const run = status.latestRun;
          if (isActive(run)) continue;
          if (run?.status === 'failed') {
            const msg = run.errorMessage ?? '';
            setState((p) => ({ ...p, runError: msg || t('dayOpen.runFailed') }));
            showErrorToast(new Error(`POST /api/day-open/run failed: ${msg}`), {
              title: t('dayOpen.runFailed'),
              context: `${tenant.displayName} run #${run.runId}`,
            });
            return false;
          }
          // Succeeded. Day-open just rewrote dbo.Parameters. Re-read the SESSION's current
          // tenant so the StatusFooter reflects it; best-effort - a stale footer must never
          // surface as a day-open failure.
          void refreshValuationParams().catch(() => undefined);
          return true;
        }
      } finally {
        pollRef.current = null;
        if (mountedRef.current) setState((p) => ({ ...p, running: false }));
      }
    })();
    pollRef.current = loop;
    return loop;
  }, [tenant.tenantId, tenant.displayName, applyStatus]);

  const load = useCallback(async () => {
    setState((p) => ({ ...p, loading: true, error: null }));
    try {
      const status = await getDayOpenStatus(tenant.tenantId);
      applyStatus(status);
      // A run is already in flight (page reloaded / opened from another tab): resume watching it.
      if (isActive(status.latestRun)) void pollUntilDone();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setState((p) => ({ ...p, loading: false, error: message }));
    }
  }, [tenant.tenantId, applyStatus, pollUntilDone]);

  const run = useCallback(async (params?: DayOpenRunParams): Promise<boolean> => {
    setState((p) => ({ ...p, running: true, runError: null }));
    try {
      await runDayOpen(tenant.tenantId, params);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        // Someone (or an earlier click) already has a run going: just watch it.
        const msg = err.serverMessage ?? t('dayOpen.alreadyRunning');
        setState((p) => ({ ...p, runError: msg }));
        return pollUntilDone();
      }
      const message = err instanceof Error ? err.message : String(err);
      setState((p) => ({ ...p, running: false, runError: message }));
      showErrorToast(err, { title: t('dayOpen.runFailed') });
      return false;
    }
    // Show "in progress" right away (the first poll is ~3s away), then wait for the outcome.
    setFetchedAt(Date.now());
    setState((p) => ({
      ...p,
      status: p.status
        ? { ...p.status, latestRun: { ...(p.status.latestRun ?? PLACEHOLDER_RUN), status: 'queued', elapsedMs: 0, errorMessage: null } }
        : p.status,
    }));
    return pollUntilDone();
  }, [tenant.tenantId, pollUntilDone]);

  // Expose an imperative handle so the page-level "open all" can read the
  // latest status and trigger a run per tenant.
  useEffect(() => {
    registerHandle(tenant.tenantId, {
      refresh: load,
      tenantId: tenant.tenantId,
      getStatus: () => stateRef.current.status,
      run,
    });
    return () => registerHandle(tenant.tenantId, null);
  }, [tenant.tenantId, load, run, registerHandle]);

  useEffect(() => {
    void load();
  }, [load]);

  const { status, loading, error, running, runError } = state;
  const activeRun = running && isActive(status?.latestRun) ? status!.latestRun : null;

  // 1s ticker for the elapsed-time display, only while a run is active.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  return (
    <section className="day-open-panel">
      <h2 className="day-open-panel__header">{tenant.displayName}</h2>

      {loading && !status && (
        <div className="day-open-panel__muted">{t('common.loading')}</div>
      )}

      {!loading && error && (
        <div className="day-open-panel__alert day-open-panel__alert--err">
          {t('dayOpen.loadError')}: {error}
        </div>
      )}

      {!error && status && (
        <>
          <div className="day-open-panel__rows">
            <div className="day-open-panel__row">
              <span className="day-open-panel__row-label">{t('dayOpen.bankData')}</span>
              <span className="day-open-panel__row-value" data-testid="last-bank-import">
                {formatBankImport(status)}
              </span>
            </div>
            <div className="day-open-panel__row">
              <span className="day-open-panel__row-label">
                {t('dayOpen.dayStatus')}
              </span>
              <span
                className={`day-open-panel__status day-open-panel__status--${
                  status.isDayOpen ? 'open' : 'closed'
                }`}
              >
                {status.isDayOpen ? t('dayOpen.open') : t('dayOpen.closed')}
              </span>
            </div>
            {status.dayOpenLast && (
              <div className="day-open-panel__row">
                <span className="day-open-panel__row-label">
                  {t('dayOpen.lastOpen')}
                </span>
                <span className="day-open-panel__row-value">
                  {formatDate(status.dayOpenLast)}
                  {status.dayOpenByUserName
                    ? ` ${t('dayOpen.by')} ${status.dayOpenByUserName}`
                    : ''}
                </span>
              </div>
            )}
            {(status.latestRun?.status === 'succeeded' || status.latestRun?.status === 'failed') &&
              status.latestRun.durationMs != null && (
                <div className="day-open-panel__row" data-testid={`day-open-last-duration-${tenant.tenantId}`}>
                  <span className="day-open-panel__row-label">{t('dayOpen.lastDuration')}</span>
                  <span className="day-open-panel__row-value">
                    {formatElapsed(status.latestRun.durationMs)} {t('dayOpen.minutesAbbr')}
                    {status.latestRun.status === 'failed' ? ` (${t('dayOpen.lastDurationFailed')})` : ''}
                  </span>
                </div>
              )}
          </div>

          <LopButton
            variant="primary"
            className="day-open-panel__run"
            onClick={() => onRequestRun(tenant, status)}
            loading={running}
            data-testid={`day-open-run-${tenant.tenantId}`}
          >
            {t('dayOpen.runButton')}
          </LopButton>

          {activeRun && (
            <div
              className="day-open-panel__progress"
              role="status"
              data-testid={`day-open-progress-${tenant.tenantId}`}
            >
              <Loader2 className="day-open-panel__spinner" size={16} aria-hidden="true" />
              <span>
                {activeRun.status === 'queued' ? t('dayOpen.queued') : t('dayOpen.inProgress')}
              </span>
              <span className="day-open-panel__elapsed" data-testid={`day-open-elapsed-${tenant.tenantId}`}>
                {t('dayOpen.elapsed')} {formatElapsed(activeRun.elapsedMs + (Date.now() - fetchedAt))}
              </span>
            </div>
          )}

          {runError && (
            <div
              className="day-open-panel__alert day-open-panel__alert--err"
              data-testid={`day-open-error-${tenant.tenantId}`}
            >
              {runError}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// Used only to give the optimistic "queued" state a complete object before the first poll answers.
const PLACEHOLDER_RUN: DayOpenRun = {
  runId: 0,
  status: 'queued',
  israeliValuationDate: null,
  israeliValuationType: null,
  foreignValuationDate: null,
  foreignValuationType: null,
  requestedAt: '',
  startedAt: null,
  finishedAt: null,
  durationMs: null,
  errorMessage: null,
  elapsedMs: 0,
};

type ParamsModalState =
  | { mode: 'single'; tenant: Tenant; status: DayOpenStatus }
  | { mode: 'all' }
  | null;

type ConfirmState = { target: NonNullable<ParamsModalState>; groups: MissingGroup[] } | null;

export function DayOpenPage() {
  const { tenants, loading, error } = useTenants();
  const handlesRef = useRef<Map<number, TenantPanelHandle>>(new Map());
  const [openAllMsg, setOpenAllMsg] = useState<string | null>(null);
  const [modal, setModal] = useState<ParamsModalState>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  const registerHandle = useCallback(
    (tenantId: number, handle: TenantPanelHandle | null) => {
      if (handle) handlesRef.current.set(tenantId, handle);
      else handlesRef.current.delete(tenantId);
    },
    [],
  );

  // Both entry points check the preliminary actions fresh (the returned promise keeps the
  // clicked LopButton busy); anything missing goes through the confirm modal first, then the
  // unchanged valuation-params flow.
  const proceed = useCallback(async (target: NonNullable<ParamsModalState>, tenants: Tenant[]) => {
    const groups = (await Promise.all(tenants.map((tn) => findMissingPrereqs(tn.tenantId, tn.displayName))))
      .filter((g) => g.items.length > 0);
    if (groups.length === 0) setModal(target);
    else setConfirm({ target, groups });
  }, []);

  // Per-tenant "Run" opens the params modal (pre-filled from that tenant's status).
  const onRequestRun = useCallback(
    (tenant: Tenant, status: DayOpenStatus) => proceed({ mode: 'single', tenant, status }, [tenant]),
    [proceed],
  );

  // "Open all" opens ONE modal; the chosen params apply to every loaded tenant.
  const handleOpenAll = useCallback(async () => {
    const ready = Array.from(handlesRef.current.values()).filter((h) => !!h.getStatus());
    if (ready.length === 0) {
      setOpenAllMsg(t('dayOpen.openAllNoneReady'));
      return;
    }
    setOpenAllMsg(null);
    const readyIds = new Set(ready.map((h) => h.tenantId));
    await proceed({ mode: 'all' }, (tenants ?? []).filter((tn) => readyIds.has(tn.tenantId)));
  }, [proceed, tenants]);

  const confirmParams = useCallback(
    async (params: DayOpenRunParams) => {
      const current = modal;
      setModal(null);
      if (!current) return;
      if (current.mode === 'single') {
        await handlesRef.current.get(current.tenant.tenantId)?.run(params);
        return;
      }
      // mode 'all' — run every loaded tenant with the same params.
      const ready = Array.from(handlesRef.current.values()).filter((h) => !!h.getStatus());
      const results = await Promise.all(ready.map((h) => h.run(params)));
      const failed = results.filter((ok) => !ok).length;
      setOpenAllMsg(
        t('dayOpen.openAllResult', { ok: String(results.length - failed), failed: String(failed) }),
      );
    },
    [modal],
  );

  return (
    <div className="day-open">
      <div className="day-open__head">
        <div>
          <h1 className="day-open__title">{t('dayOpen.pageTitle')}</h1>
          <p className="day-open__subtitle">{t('dayOpen.subtitle')}</p>
        </div>
        <LopButton
          variant="primary"
          className="day-open__open-all"
          onClick={handleOpenAll}
          disabled={loading || !tenants || tenants.length === 0}
        >
          {t('dayOpen.openAllReady')}
        </LopButton>
      </div>

      {openAllMsg && (
        <div className="day-open__open-all-msg">{openAllMsg}</div>
      )}

      {loading && <div className="day-open__muted">{t('common.loading')}</div>}

      {!loading && error && (
        <div className="day-open__alert day-open__alert--err">{error}</div>
      )}

      {!loading && !error && tenants && tenants.length === 0 && (
        <div className="day-open__muted">{t('dayOpen.noTenants')}</div>
      )}

      {!loading && !error && tenants && tenants.length > 0 && (
        <div className="day-open__grid">
          {tenants.map((tenant) => (
            <TenantPanel
              key={tenant.tenantId}
              tenant={tenant}
              registerHandle={registerHandle}
              onRequestRun={onRequestRun}
            />
          ))}
        </div>
      )}

      {confirm && (
        <DayOpenConfirmModal
          groups={confirm.groups}
          showTenant={confirm.target.mode === 'all'}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            setModal(confirm.target);
            setConfirm(null);
          }}
        />
      )}

      {modal && (
        <ValuationParamsModal
          title={
            modal.mode === 'single'
              ? t('dayOpen.params.titleSingle', { tenant: modal.tenant.displayName })
              : t('dayOpen.params.titleAll')
          }
          initial={
            modal.mode === 'single'
              ? {
                  israeliValuationDate: modal.status.israeliValuationDate,
                  israeliValuationType: modal.status.israeliValuationType,
                  foreignValuationDate: modal.status.foreignValuationDate,
                  foreignValuationType: modal.status.foreignValuationType,
                }
              : undefined
          }
          onConfirm={(p) => void confirmParams(p)}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
